import { IonCard, IonCol, IonGrid, IonItem, IonPopover, IonRow, useIonPopover } from "@ionic/react";
import { ComponentProps, MouseEventHandler, useCallback, useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import Select from "react-select";

import CircleButton from "@components/buttons/CircleButton";
import RichTextareaMenuButton from "@components/buttons/RichTextareaMenuButton";
import AttachmentPreview from "@components/fields/AttachmentPreview";
import RichTextareaExtraControlsPopover from "@components/fields/RichTextareaExtraControlsPopover";
import RichTextareaHyperlinkForm from "@components/fields/RichTextareaHyperlinkForm";
import RichTextareaInAppLinksSelect from "@components/fields/RichTextareaInAppLinksSelect";
import styles from "@components/fields/RichTextareaMenu.module.css";
import { RichTextareaContext } from "@components/fields/RichTextareaProvider";
import RichTextareaSubmitButton from "@components/fields/RichTextareaSubmitButton";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import { editorSelectStyles, extraRichTextareaMenuControls, RichTextareaMenuControls } from "@utils/formUtils";
import { generateHTMLId } from "@utils/htmlUtils";

type Props = ComponentProps<typeof RichTextareaSubmitButton> &
  Pick<ComponentProps<typeof RichTextareaInAppLinksSelect>, "showArticleLinksEvenWithoutJourneyId"> & {
    controls: RichTextareaMenuControls[];
    disabled?: boolean;
  };

const RichTextareaMenu = ({
  controls,
  disabled,
  showArticleLinksEvenWithoutJourneyId,
  submitDisabled = false,
  submitVariant
}: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();

  const {
    editor,
    editorRefresher,
    insertContent,
    journeyId,
    lastAnchorPoint,
    overwriteContent,
    setInsertContent,
    setLastAnchorPoint,
    setOverwriteContent,
    setUploads,
    setUploadsInProgress,
    setWarning,
    uploads,
    warning
  } = useContext(RichTextareaContext);

  // The controller-based popover is required, because the link popover content includes a form, and
  // you can't put a form inside a form (the controller-based popover will be mounted to IonApp).
  // Note that the controller-based popover won't work in Ladle, and it can't consume the
  // RichTextareaContext.
  const linkPopoverOpener = useOpener();
  const [presentLinkFormPopover, dismissLinkFormPopover] = useIonPopover(RichTextareaHyperlinkForm, {
    dismiss: () => dismissLinkFormPopover(),
    editor,
    isOpen: linkPopoverOpener.isOpen
  });

  const [extraControlsTriggerId] = useState<string>(generateHTMLId());

  const extraControls = controls.filter(c => {
    // clients should never see in-app links or templates, even if a form that allows them is
    // exposed to a client
    if (c === "inAppLinks" && !!myClient) return false;
    if (c === "templates" && !!myClient) return false;
    // also hide in app links if there's no journey and you're not allowed to show them without the
    // journey
    if (c === "inAppLinks" && !journeyId && !showArticleLinksEvenWithoutJourneyId) return false;

    return extraRichTextareaMenuControls.includes(c);
  });
  const hasExtraControls = extraControls.length > 0;

  // The extra controls popover, however, does not contain a form, so it can be used as an inline popover.
  // This allows it to consume the RichTextareaContext so we don't have to pass it as many props.
  const extraControlsPopoverOpener = useOpener();

  const [headingSelectValue, setHeadingSelectValue] = useState(0);

  const handleLink: MouseEventHandler<HTMLIonButtonElement> = event => {
    if (editor?.isActive("link")) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      presentLinkFormPopover({
        event: event.nativeEvent,
        onDidDismiss: () => {
          editor?.commands.focus();
          linkPopoverOpener.close();
        },
        onDidPresent: () => {
          linkPopoverOpener.open();
        }
      });
    }
  };

  const handleHeading = useCallback(
    (value: number) => {
      setHeadingSelectValue(value);

      if (value === 0) {
        editor?.chain().focus().setParagraph().run();
      } else if (value === 1 || value === 2 || value === 3) {
        editor?.chain().focus().toggleHeading({ level: value }).run();
      }
    },
    [editor]
  );

  const headingSelectValues = [
    { label: "Normal", value: 0 },
    { label: "H2", value: 2 },
    { label: "H3", value: 3 }
  ];

  useEffect(() => {
    editor?.on("selectionUpdate", () => {
      if (headingSelectValue !== 1 && editor.isActive("heading", { level: 1 })) {
        setHeadingSelectValue(1);
      } else if (headingSelectValue !== 2 && editor.isActive("heading", { level: 2 })) {
        setHeadingSelectValue(2);
      } else if (headingSelectValue !== 3 && editor.isActive("heading", { level: 3 })) {
        setHeadingSelectValue(3);
      }
      if (headingSelectValue !== 0 && !editor.isActive("heading")) {
        setHeadingSelectValue(0);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headingSelectValue]);

  const extraControlsPopoverClassNames = [
    styles.extraControlsPopover,
    styles[`extraControlsPopoverWith${extraControls.length}Items`]
  ];

  if (!editor || controls.length === 0) return null;

  return (
    <>
      <IonGrid className="ion-no-padding" onClick={e => e.stopPropagation()}>
        {warning && (
          <IonRow className="ion-align-items-center">
            <IonCol className="ion-no-padding" size="12">
              <IonCard className={styles.warningCard} color="warning">
                <IonItem color="warning" lines="none">
                  {warning}
                  <CircleButton color="yellow" icon="xmark" onClick={() => setWarning(null)} slot="end" />
                </IonItem>
              </IonCard>
            </IonCol>
          </IonRow>
        )}
        {uploads.length > 0 && (
          <IonRow className="ion-align-items-center">
            {uploads.map(upload => (
              <IonCol className="ion-no-padding" key={upload.upload.blobId} size="12">
                <AttachmentPreview
                  onComplete={() => {
                    setUploadsInProgress(currentUploads => currentUploads.filter(u => u !== upload.file));
                  }}
                  onDelete={() => {
                    setUploadsInProgress(currentUploads => currentUploads.filter(u => u !== upload.file));
                    setUploads(currentUploads => currentUploads.filter(u => u !== upload));
                  }}
                  uploadWithFile={upload}
                />
              </IonCol>
            ))}
          </IonRow>
        )}
        <IonRow className="ion-align-items-center">
          <IonCol className="ion-flex-grow-1 ion-no-padding" size="auto">
            <IonGrid className="ion-no-padding">
              <IonRow className="ion-align-items-center">
                <IonCol className="ion-no-padding" size="auto">
                  {controls.includes("bold") && (
                    <RichTextareaMenuButton
                      active={editor.isActive("bold")}
                      disabled={disabled}
                      icon="bold"
                      onClick={() => editor.chain().focus().toggleBold().run()}
                      translationId="accessibility.ariaLabels.bold"
                    />
                  )}
                  {controls.includes("italic") && (
                    <RichTextareaMenuButton
                      active={editor.isActive("italic")}
                      disabled={disabled}
                      icon="italic"
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                      translationId="accessibility.ariaLabels.italic"
                    />
                  )}
                  {controls.includes("underline") && (
                    <RichTextareaMenuButton
                      active={editor.isActive("underline")}
                      disabled={disabled}
                      icon="underline"
                      onClick={() => editor.chain().focus().toggleUnderline().run()}
                      translationId="accessibility.ariaLabels.underline"
                    />
                  )}
                  {controls.includes("bulletList") && (
                    <RichTextareaMenuButton
                      active={editor.isActive("bulletList")}
                      disabled={disabled}
                      icon="listUl"
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                      translationId="accessibility.ariaLabels.bulletList"
                    />
                  )}
                  {controls.includes("orderedList") && (
                    <RichTextareaMenuButton
                      active={editor.isActive("orderedList")}
                      disabled={disabled}
                      icon="listOl"
                      onClick={() => editor.chain().focus().toggleOrderedList().run()}
                      translationId="accessibility.ariaLabels.orderedList"
                    />
                  )}
                  {controls.includes("link") && (
                    <RichTextareaMenuButton
                      active={editor.isActive("link")}
                      disabled={disabled || (!editor.isActive("link") && editor.view.state.selection.empty)}
                      icon="link"
                      onClick={handleLink}
                      translationId="accessibility.ariaLabels.link"
                    />
                  )}
                </IonCol>
                {hasExtraControls && (
                  <IonCol>
                    <CircleButton
                      aria-label={intl.formatMessage({ id: "dictionary.more" })}
                      disabled={disabled}
                      icon="plus"
                      id={extraControlsTriggerId}
                      onClick={() => {
                        // when the popover appears, it takes focus from the editor and the editor
                        // won't remember the last cursor position. so we need to save it here and
                        // restore it after the popover closes.
                        setLastAnchorPoint(editor.state.selection.anchor);
                      }}
                    />
                  </IonCol>
                )}

                {controls.includes("headings") && (
                  <IonCol className="ion-no-padding" size="auto">
                    <Select
                      isDisabled={disabled}
                      isSearchable={false}
                      menuPlacement="top"
                      menuPortalTarget={document.body}
                      onChange={option => handleHeading(option?.value ?? 0)}
                      options={headingSelectValues}
                      styles={editorSelectStyles}
                      value={headingSelectValues.find(option => option.value === headingSelectValue)}
                    />
                  </IonCol>
                )}
              </IonRow>
            </IonGrid>
          </IonCol>
          {controls.includes("submit") && (
            <IonCol className="ion-no-padding" size="auto">
              <RichTextareaSubmitButton submitDisabled={submitDisabled} submitVariant={submitVariant} />
            </IonCol>
          )}
        </IonRow>
      </IonGrid>
      {hasExtraControls && (
        <IonPopover
          alignment="center"
          className={extraControlsPopoverClassNames.join(" ")}
          isOpen={extraControlsPopoverOpener.isOpen}
          onIonPopoverDidDismiss={() => {
            extraControlsPopoverOpener.close();
            if (overwriteContent) {
              // use clearContent then insertContent because insertContent will update the hook-form
              // state, but setContent will not ¯\_(ツ)_/¯
              editor.commands.clearContent();
              editor.commands.insertContent(overwriteContent);
              setOverwriteContent(null);
            }
            if (insertContent) {
              if (lastAnchorPoint) {
                editor.commands.insertContentAt(lastAnchorPoint, insertContent);
              } else {
                editor.commands.insertContent(insertContent);
              }
              setInsertContent(null);
            }
            setLastAnchorPoint(null);

            editorRefresher?.toggle();

            // ugh so friggin annoying: the editor cannot actually receive focus until the popover has
            // fully gone away, which is not the case if you execute this command synchronously
            setTimeout(() => editor.commands.focus(), 0);
          }}
          onIonPopoverDidPresent={extraControlsPopoverOpener.open}
          onIonPopoverWillPresent={extraControlsPopoverOpener.open}
          side="top"
          trigger={extraControlsTriggerId}
        >
          <RichTextareaExtraControlsPopover
            controls={extraControls}
            dismiss={extraControlsPopoverOpener.close}
            showArticleLinksEvenWithoutJourneyId={showArticleLinksEvenWithoutJourneyId}
          />
        </IonPopover>
      )}
    </>
  );
};

export default RichTextareaMenu;
